/**
 * The playground's boot and wiring.
 *
 * session → spec → client → links and roster → the stores and the Director →
 * the conductor and renderer → actions and gestures → the router. Every
 * module is built through its factory in contracts.ts; this file owns only
 * how they meet:
 *
 * - the AppContext every screen receives, with the bus screens ask each
 *   other through and the tab's transport;
 * - which screens a route mounts, and where the shell lays them out;
 * - the scene the Fx draws: every screen's published layouts, merged, over
 *   the matter as scrubbed, with pinned occupiers and names for orbs;
 * - the links: every one, whoever opens it, feeds the reconciler and names
 *   its peers for the Fx (app/wiring.ts); our own presence frames go on the
 *   socket of the World they are about; all of them close when key A goes;
 * - the beats of calls this tab's hand did not send (the Director's, a
 *   scenario's), and the fix-its chosen on a refusal's sentence;
 * - the verb ring the hand opens, which chooses through the hand's machine;
 * - the race line a refusal's voice says: who sealed the slots first (§7 rule 5);
 * - what happens to the session when its key is revoked, rotated or refused.
 */

import type {
  AppContext, Args, Attribution, CallRecord, CarryEffect, CarryState, FieldLayout, Flip, Gestures, Held, Hole, PresenceIn,
  Route, Scene, SpecSource, Target, Unmount, Verb, Viewer,
} from './contracts';
import { createSession } from './session';
import { createSpecSource } from './spec/load';
import { createClient } from './net/client';
import { createLinks } from './net/links';
import { createDirector } from './net/director';
import { createPlaneStore } from './model/planes';
import { createRecordStore, occupiersOf } from './model/records';
import { createReconciler } from './model/reconcile';
import { createRunner } from './usecases/runner';
import { createRecorder } from './usecases/recorder';
import { vet } from './usecases/vet';
import { createUseCaseStore } from './usecases/store';
import { createFx } from './fx/fx';
import { createGestures, scrubs } from './gesture/index';
import { createBus } from './app/bus';
import { createTransport } from './app/transport';
import {
  createActionsWith, guardSession, raceLine, raceSeal, RACE_MS, RACE_WAIT_MS, sealsOf, type KeySafety, type Seal, type VerdictCard,
} from './app/actions';
import { attachingLinks, playRemoteBeats, sendPresence, takeFixit } from './app/wiring';
import { createRouter, freshRelayName, needsKey, worldOfRoute } from './app/route';
import { handTry, tryFromSearch } from './app/sandbox';
import { createTaps } from './net/tap';
import { worldKey } from './worlds';
import { bindRegions, createAnnouncer, isTyping, modalOpen, prefersReducedMotion } from './ui/a11y';
import {
  buildScene, createShell, hintsOf, mountForge, mountGates, mountHud, mountTabs, prefs, showGlossary, type RingExtra,
} from './ui/shell';
import { mount as mountDirector } from './stage/director';
import { mount as mountInspector } from './stage/inspector';
import { mount as mountWireLog, settleAnswer } from './stage/wire-log';
import { h, mount as fill } from '../../console/src/dom';
import { sheetFor } from './ui/sheets';
import { isObj } from './model/json';

declare const PLAYGROUND_API_BASE: string | undefined;

/** The API this build talks to: fixed at build time; a local wrangler when the define is missing. */
const apiBase = (): string =>
  (typeof PLAYGROUND_API_BASE === 'string' && PLAYGROUND_API_BASE ? PLAYGROUND_API_BASE : 'http://127.0.0.1:8787').replace(/\/+$/, '');

const IDLE: CarryState = { phase: 'idle', mods: new Set(), candidates: [], index: 0 };

const el = <T extends HTMLElement>(id: string, make: () => T): T => {
  const found = document.getElementById(id) as T | null;
  if (found) return found;
  const made = make();
  made.id = id;
  document.body.append(made);
  return made;
};

const safeLocal = (): Storage | null => {
  try {
    return localStorage;
  } catch {
    return null;
  }
};

/** Mount a screen so a failing one shows its failure in place instead of taking the app down. */
const safely = (name: string, root: HTMLElement, run: () => Unmount): Unmount => {
  try {
    return run();
  } catch (error) {
    fill(root, h('div', { class: 'screen-fault', role: 'alert' },
      h('p', { class: 'eyebrow' }, `${name.toUpperCase()} · COULD NOT OPEN`),
      h('p', {}, String(error instanceof Error ? error.message : error))));
    return () => root.replaceChildren();
  }
};

/**
 * The screens a route shows, and the Deck and MCP drawers, are chunks of their own (§4.5): the
 * page is interactive on the chrome, the hand and the Fx, and each screen arrives when it is
 * first wanted. The drawers load when they are first opened, or once the page is idle.
 */
const SCREENS = {
  connect: () => import('./stage/connect'),
  constellation: () => import('./stage/constellation'),
  field: () => import('./stage/field'),
  river: () => import('./stage/river'),
  roster: () => import('./stage/roster'),
  loom: () => import('./stage/loom'),
  inbox: () => import('./stage/inbox'),
  gallery: () => import('./stage/gallery'),
  account: () => import('./stage/account'),
  deck: () => import('./stage/deck'),
  mcp: () => import('./stage/mcp-lens'),
} as const;

/** Calls the hand made from the stage (not a panel that shows its own answers): their shown-once secrets land in the shell's capsule. */
const HAND_VIAS = new Set(['pointer', 'click', 'keyboard']);

async function boot(): Promise<void> {
  const canvas = el('gl', () => h('canvas'));
  const appRoot = el('app', () => h('div'));
  const overlay = el('overlay', () => h('div'));
  const sheets = el('sheets', () => h('div'));
  const announce = createAnnouncer(
    el('live-polite', () => h('div', { class: 'sr-only', 'aria-live': 'polite' })),
    el('live-assertive', () => h('div', { class: 'sr-only', 'aria-live': 'assertive' })),
  );
  const params = new URLSearchParams(location.search);
  const reduced = prefersReducedMotion();

  const fx = createFx({ reduced, sound: prefs.sound(), perfHud: params.get('perf') === '1', forceCanvas2d: params.get('gl') === '0' });
  prefs.bind(fx, reduced);
  fx.mount(canvas, overlay);
  const shell = createShell({ root: appRoot, sheets, fx });
  shell.splash('Tuning the bit-sea…');
  bindRegions(document, announce);

  const session = createSession(apiBase());
  // The offline reel's recording, fetched only when someone opens the reel.
  let spec: SpecSource;
  try {
    spec = await createSpecSource(session.apiBase);
  } catch (error) {
    shell.splash(`The API's spec could not be read (${String(error)}). Reload to try again.`);
    return;
  }
  const catalogue = () => spec.current();
  const bus = createBus();
  const transport = createTransport(safeLocal());
  const client = createClient({ session, catalogue });
  const pool = createLinks({ session, client });
  const roster = pool.roster;
  const director = createDirector({ session, client });
  const planes = createPlaneStore();
  const records = createRecordStore(client);
  // A website link to `/?try=1` starts the try: the search becomes the hash route before the router reads it.
  const tryHash = tryFromSearch(location.search);
  if (tryHash) history.replaceState(null, '', `${location.pathname}${tryHash}`);
  const router = createRouter();
  // A Tap dropped on a World ring mints a ticket, and its raw socket opens with it; the same drop again unplugs it (§3.2 rows 29–30).
  const taps = createTaps({ apiBase: session.apiBase });
  const unplugging = new Set<string>();
  client.on((record, phase) => {
    if (phase !== 'sent' || record.op !== 'watchTicket' || record.source.kind !== 'hand' || typeof record.args.w !== 'string') return;
    const world = worldKey(record.persona, record.args.w);
    if (taps.close(world)) unplugging.add(world);
  });
  /** A hand's ticket, as the caller got it: its tap opens, or, when the drop unplugged one, it goes unused. */
  const tapWith = (record: CallRecord): void => {
    if (record.op !== 'watchTicket' || record.source.kind !== 'hand' || typeof record.args.w !== 'string') return;
    const world = worldKey(record.persona, record.args.w);
    if (unplugging.delete(world)) { announce.polite(`Unplugged the tap on ${world}.`); return; }
    const body = record.answer?.body;
    const ticket = isObj(body) && typeof body.ticket === 'string' ? body.ticket : null;
    if (!ticket) return;
    taps.open(world, ticket);
    announce.polite(`Tapped ${world}: its raw frames stream into the Wire. Tap it again, or press L on its ring, to unplug.`);
  };

  // Every link, whoever opens it (a route, the Loom's hands, the reconciler for a persona's World),
  // feeds the reconciler and names its peers for the Fx.
  const linkWatchers = new Set<() => void>();
  let reconcilerRef: { nudge: AppContext['reconciler']['nudge'] } = { nudge: () => undefined };
  let sayFocus: (world: string) => void = () => undefined;
  const links = attachingLinks(pool.links, {
    reconciler: { nudge: (w, n) => reconcilerRef.nudge(w, n) },
    fx,
    changed: () => { for (const fn of [...linkWatchers]) fn(); },
    // A socket opens after its World's screen says where we are: say it again once peers can hear it.
    live: (w) => sayFocus(w),
  });
  const reconciler = createReconciler({ client, planes, records, links });
  reconcilerRef = reconciler;
  const runner = createRunner({ client, catalogue, session, vet, now: () => reconciler.serverNow() });
  const recorder = createRecorder(client);
  const usecases = createUseCaseStore(safeLocal());

  const ownKeyId = (): string | null => {
    const me = records.get(null, 'me');
    return isObj(me) && isObj(me.key) && typeof me.key.id === 'string' ? me.key.id : null;
  };

  // -- focus, from the route
  const focus = (): { world?: string; date?: string; hour?: number } => {
    const r = router.current();
    const world = worldOfRoute(r);
    if (!world) return {};
    return r.name === 'world'
      ? { world, ...(r.date ? { date: r.date } : {}), ...(r.hour !== undefined ? { hour: r.hour } : {}) }
      : { world };
  };

  // -- the scene: every owner's layouts, the selection, the carry's dashed hint, over the matter as scrubbed
  const layouts = new Map<string, readonly FieldLayout[]>();
  let selection: Scene['selection'] = [];
  let hints: Scene['hints'] = [];
  let sceneQueued = false;
  const occupierOf = (world: string, match: (o: ReturnType<typeof occupiersOf>[number]) => boolean) =>
    occupiersOf(records.get(world, 'occupiers')).find(match);
  const pushScene = (): void => {
    if (sceneQueued) return;
    sceneQueued = true;
    queueMicrotask(() => {
      sceneQueued = false;
      fx.setScene(buildScene({
        layouts, selection, hints,
        matter: (w) => scrubs.matter(w, planes.get(w)),
        pinned: (w, id) => occupierOf(w, (o) => o.id === id)?.pinned === true,
        occupierId: (w, name) => occupierOf(w, (o) => o.name === name)?.id ?? null,
      }));
    });
  };
  scrubs.on(() => pushScene());
  records.on((_w, kind) => { if (kind === 'occupiers' || kind === 'describe') pushScene(); });

  /** A frame goes on the socket of the World it is about, else the World in focus; never from a hidden tab. */
  const presence = (frame: PresenceIn, world: string | undefined): void => {
    sendPresence(links, frame, world, focus().world, document.hidden);
  };

  // -- drafts, the verb ring, the Inspector
  const openDraft = (op: string, partial: Args, need?: readonly Hole[]): void => {
    const body = isObj(partial.body) ? partial.body : {};
    // A new World is named in place on the stage; any other draft opens on its card in the Deck.
    if (op === 'createWorld' && router.current().name === 'stage'
      && bus.emit('createWorld', { preset: typeof body.preset === 'string' ? body.preset : null, partial })) return;
    shell.drawer('deck');
    // A Deck still on its way takes the draft when it mounts.
    if (!bus.emit('draft', { op, partial, need: need ?? [] }, { keep: true })) {
      announce.polite(`${op}: fill ${need && need.length ? need.map((x) => x.name).join(', ') : 'its holes'} on its card in the Deck`);
    }
  };
  const openPalette = (): void => {
    const before = document.activeElement as HTMLElement | null;
    const deckWasOpen = appRoot.querySelector<HTMLElement>('.app')?.dataset.drawer === 'deck';
    shell.drawer('deck');
    // The palette gives focus back to where it was taken from: where ⌘K was pressed, not the drawer it opened.
    if (!deckWasOpen && before && before !== document.body && before.isConnected) before.focus();
    // Closed with nothing chosen, the palette takes the Deck it opened with it (it never stays over the stage).
    bus.emit('palette', { dismissed: () => { if (!deckWasOpen) shell.drawer(null); } }, { keep: true });
  };

  // The verb ring. The hand opens it (Enter, right-click, long-press) and stays in its ring phase until
  // an entry is chosen, which the hand's machine performs, or the menu closes, which backs the hand out.
  let gestures: Gestures | null = null;
  let closeRing: (() => void) | null = null;
  const isHeldThing = (x: Held | Target): x is Held => typeof (x as Held).label === 'string';
  /** What a ring offers beyond the hand's verbs: a World's ring dives in, a braid opens its Loom. */
  const ringExtras = (subject: Held | Target): RingExtra[] => {
    // The DECK and MCP buttons are drop targets too: Enter on one still opens what it names.
    if (!isHeldThing(subject) && subject.id === 'deck') return [{ label: 'Open the Deck', hint: 'every operation as a card (⌘K)', run: () => shell.drawer('deck') }];
    if (!isHeldThing(subject) && subject.id === 'gate:mcp') return [{ label: 'Open the MCP lens', hint: 'initialize, tools/list, tools/call (M)', run: () => shell.drawer('mcp') }];
    if (!isObj(subject.data)) return [];
    const d = subject.data;
    if (subject.kind === 'ring' && !isHeldThing(subject) && subject.world && d.remote !== true) {
      const world = subject.world;
      return [
        {
          label: `Dive into ${world}`, hint: 'the World up close',
          // On the position the ring names, as a click dives.
          run: () => router.go({
            name: 'world', world,
            ...(typeof d.diveDate === 'string' ? { date: d.diveDate } : {}),
            ...(typeof d.diveHour === 'number' ? { hour: d.diveHour } : {}),
          }),
        },
        // The spine's gesture (spine ⇢ planner) by keyboard and touch: the Loom opens as a builder.
        { label: `Plan a relay on ${world}`, hint: 'the Loom, as a builder', run: () => router.go({ name: 'relay', world, relay: freshRelayName() }) },
      ];
    }
    if (subject.kind === 'braid' && typeof d.world === 'string' && typeof d.relay === 'string') {
      const world = d.world;
      const relay = d.relay;
      return [{ label: `Open the Loom of ${relay}`, hint: 'the relay up close', run: () => router.go({ name: 'relay', world, relay }) }];
    }
    return [];
  };
  const showRing = (verbs: readonly Verb[], subject: Held | Target): void => {
    closeRing = shell.ring(verbs, {
      onPick: (_verb, index) => {
        closeRing = null;
        gestures?.choose(index);
      },
      onClose: () => {
        closeRing = null;
        if (gestures?.state().phase === 'ring') gestures.cancel();
      },
      extras: ringExtras(subject),
      // A relay's first entry opens its Loom: Enter on a chip never lands on "Withdraw every ask".
      lead: subject.kind === 'braid',
    });
  };

  // -- the session's key
  let panelsOff: Unmount | null = null;
  let screenOff: Unmount | null = null;
  const closeLinks = (): void => links.closeAll();
  /** Back to Connect. A key the API refused takes only itself; Disconnect takes every key this tab holds, the Anthropic key too. */
  const leave = (why: string | null, everything = false): void => {
    if (everything) session.forgetAll();
    else session.setKey('A', null);
    screenOff?.();
    screenOff = null;
    panelsOff?.();
    panelsOff = null;
    closeLinks();
    taps.closeAll();
    router.go({ name: 'connect' });
    if (why) {
      shell.notice(why, 'warn');
      announce.assertive(why);
    }
  };
  const keySafety: KeySafety = {
    ended: (why) => { if (session.key('A')) leave(why); },
    rotated: (secret, id) => {
      if (session.key('A') === secret) return;
      session.setKey('A', secret);
      void records.refresh(null, 'me');
      const line = `Your key was rotated: this tab now uses its successor${id ? ` ${id}` : ''}.`;
      shell.notice(line, 'ok');
      announce.polite(line);
    },
    unauthorized: (p) => {
      if (p !== 'A') {
        if (!session.key(p)) return;
        session.setKey(p, null);
        shell.notice(`Persona ${p}'s key no longer works, so it was dropped.`, 'warn');
        return;
      }
      if (!session.key('A')) return;
      const here = router.current().name;
      // Connect checks keys itself and says why a key failed; it only needs the key gone.
      if (here === 'connect' || here === 'verify' || here === 'try') session.setKey('A', null);
      else leave('This key no longer works. Connect again with one that does.');
    },
  };

  // -- races: seals kept a few seconds, so a refusal's voice can say who sealed its slots first (§7 rule 5).
  // The sentence on screen says it too: the conductor reads the same seals.
  let seals: Seal[] = [];
  const sealWaiters = new Set<() => void>();
  const peerName = (world: string) => (sid: string): string | null => roster.peers(world).find((p: Viewer) => p.sid === sid)?.name ?? null;
  /** The race line for a refusal: at once when the winner's seal is here, else after waiting up to 300 ms for it. */
  const raceFor = (card: VerdictCard): Promise<string | null> => new Promise((resolve) => {
    const world = card.world;
    if (card.kind !== 'refusal' || !world) { resolve(null); return; }
    const look = (): boolean => {
      const seal = raceSeal(seals, card, Date.now());
      if (seal) resolve(raceLine(seal, peerName(world)));
      return seal !== null;
    };
    if (look()) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const waiter = (): void => {
      if (!look()) return;
      sealWaiters.delete(waiter);
      if (timer !== null) clearTimeout(timer);
    };
    sealWaiters.add(waiter);
    timer = setTimeout(() => { sealWaiters.delete(waiter); resolve(null); }, RACE_WAIT_MS);
  });

  /** The carry as the hand last reported it. A drop's effects run once the hand is idle again, so this is the carry they ended. */
  let carried: CarryState = IDLE;
  let ctxRef: AppContext | null = null;
  const actions = createActionsWith({
    client, fx, reconciler, announce, catalogue, openDraft, presence, planes, records, ownKeyId, keySafety, transport, focus,
    confirm: (req) => shell.confirm(req),
    inspect: (subject) => ctx.inspect(subject),
    carry: () => carried,
    secret: (record) => {
      tapWith(record);
      if (!ctxRef || record.source.kind !== 'hand' || !HAND_VIAS.has(record.source.via)) return;
      settleAnswer(ctxRef, record, (secret, what) => shell.capsule({ title: 'A secret, shown once', secret, note: what }));
    },
    ui: {
      ghost: (held, at, home) => shell.ghost(held, at, home),
      ring: (verbs, subject) => showRing(verbs, subject),
      verdict: (card) => {
        void raceFor(card).then((line) => { if (line) announce.polite(line); });
      },
    },
  });
  guardSession(client, catalogue, ownKeyId, keySafety);

  const g: Gestures = createGestures({
    verbContext: () => ({ catalogue: catalogue(), planes, records, focus: focus(), ownKeyId: ownKeyId() }),
    fx, announce, effect: (e: CarryEffect) => actions.effect(e), root: appRoot,
    // A soft hold's `until` is read off the Worlds' clock (§7 rule 17).
    clock: () => reconciler.serverNow(),
  });
  gestures = g;
  g.onState((s) => {
    carried = s;
    if (s.phase !== 'ring' && closeRing) {
      const close = closeRing;
      closeRing = null;
      close();
    }
    const next = hintsOf(s);
    if (next.length === 0 && hints.length === 0) return;
    hints = next;
    pushScene();
  });

  const ctx: AppContext = {
    session, spec, catalogue, client, links, roster, planes, records, reconciler, director, runner, recorder, vet, usecases, fx,
    gestures: g, actions, router, announce, bus, transport, taps,
    confirm: (req) => shell.confirm(req),
    inspect(subject, opts) {
      bus.emit('inspect', { subject });
      if (opts?.open) shell.inspect(true);
    },
    publishLayout(owner, list) {
      if (list.length > 0) layouts.set(owner, list);
      else layouts.delete(owner);
      pushScene();
    },
    select(sel) {
      selection = sel;
      pushScene();
    },
    focus,
  };
  ctxRef = ctx;

  /** A screen from its chunk: it mounts when it arrives, unless the route moved on first. */
  const lazily = <P,>(name: string, root: HTMLElement, load: () => Promise<{ mount: (root: HTMLElement, ctx: AppContext, params: P) => Unmount }>, params: P): Unmount => {
    let off: Unmount | null = null;
    let gone = false;
    // The screen's own sheet comes with its chunk (ui/sheets.ts), so it never opens unstyled.
    const sheet = (Object.keys(SCREENS) as (keyof typeof SCREENS)[]).find((k) => SCREENS[k] === load);
    Promise.all([load(), sheet ? sheetFor(sheet) : undefined]).then(([m]) => m).then(
      (m) => { if (!gone) off = safely(name, root, () => m.mount(root, ctx, params)); },
      (error: unknown) => { if (!gone) off = safely(name, root, () => { throw error; }); },
    );
    return () => { gone = true; off?.(); };
  };

  // A fix-it chosen on a refusal's sentence runs (or opens as a draft), the World back to Live first.
  fx.onFixIt((fix, at, via) => takeFixit(actions, catalogue(), fix, at, via, scrubs));
  // The Director's calls (made on the server, injected) and a scenario's play their flight, door and verdict too.
  playRemoteBeats(client, fx, catalogue);

  reconciler.onFlips((flips: readonly Flip[], by: Attribution) => {
    const sealed = flips.filter((f) => f.cause.kind === 'commit');
    const other = flips.filter((f) => f.cause.kind !== 'commit');
    if (sealed.length > 0) fx.conductor.play({ kind: 'seal', flips: sealed, by });
    if (other.length > 0) fx.conductor.play({ kind: 'correction', flips: other });
    if (sealed.length > 0) {
      const now = Date.now();
      seals = [...seals.filter((x) => now - x.at <= RACE_MS), ...sealsOf(sealed, by, now)];
      for (const fn of [...sealWaiters]) fn();
      // A peer's commit may change what the carried thing's target will take.
      if (g.state().phase !== 'idle') g.refresh();
    }
    pushScene();
  });

  // Key A gone (Forget on Connect, a refused key, Disconnect): its sockets go with it, the pool's too.
  let keyed = session.key('A') !== null;
  session.on(() => {
    const now = session.key('A') !== null;
    if (keyed && !now) closeLinks();
    keyed = now;
  });

  // ⇧M: every tool call as MCP tools/call, or back to REST; said once, from anywhere.
  transport.on((v) => announce.polite(`calls now go as ${v === 'mcp' ? 'MCP tools/call' : 'REST'}`));

  // -- the chrome that stays while a key is held
  const ensurePanels = (): void => {
    if (panelsOff) return;
    const parts: Unmount[] = [
      mountHud(shell.hud, ctx, {
        links: () => links.worlds().flatMap((world) => {
          const link = links.get(world);
          return link ? [{ world, state: link.state() }] : [];
        }),
        onLinks: (fn) => { linkWatchers.add(fn); return () => { linkWatchers.delete(fn); }; },
        disconnect: () => leave(null, true),
        openDrawer: (which) => shell.toggleDrawer(which),
        palette: openPalette,
        inspector: () => shell.inspect('toggle'),
        inspecting: () => shell.inspecting(),
        glossary: () => showGlossary(sheets, (to) => router.go(to)),
        stopRecording: () => {
          if (!recorder.recording()) return;
          const uc = recorder.stop(`My drags · ${new Date().toISOString().slice(0, 16).replace('T', ' ')}`);
          usecases.save(uc);
          shell.notice(`Saved "${uc.title}" (${uc.steps.length} steps) to the gallery.`, 'ok');
        },
      }),
      mountForge(shell.forge, ctx),
      mountGates(shell.gates, ctx, { openDrawer: (which) => shell.toggleDrawer(which) }),
      mountTabs(shell.tabs, ctx, shell),
      safely('inspector', shell.inspector, () => mountInspector(shell.inspector, ctx, {})),
      safely('wire', shell.wire, () => mountWireLog(shell.wire, ctx, {})),
      safely('director', shell.director, () => mountDirector(shell.director, ctx, {})),
      drawers(),
    ];
    panelsOff = () => { for (const off of parts.reverse()) off(); };
  };

  /** The Deck and the MCP lens: loaded when first opened (or handed a draft or the palette), else once the page is idle. */
  const drawers = (): Unmount => {
    const offs: Unmount[] = [];
    let loaded = false;
    const load = (): void => {
      if (loaded) return;
      loaded = true;
      offs.push(lazily('deck', shell.deck, SCREENS.deck, {}), lazily('mcp lens', shell.mcp, SCREENS.mcp, {}));
    };
    const idle = (globalThis as { requestIdleCallback?: (fn: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
    const timer = idle ? null : setTimeout(load, 1500);
    if (idle) idle(load, { timeout: 2500 });
    const watch = new MutationObserver(() => { if (!shell.deck.hidden || !shell.mcp.hidden) load(); });
    watch.observe(shell.deck, { attributes: true, attributeFilter: ['hidden'] });
    watch.observe(shell.mcp, { attributes: true, attributeFilter: ['hidden'] });
    return () => {
      watch.disconnect();
      if (timer !== null) clearTimeout(timer);
      loaded = true;
      for (const off of offs.reverse()) off();
    };
  };

  // -- routes
  let opened = false;
  const render = (route: Route): void => {
    screenOff?.();
    screenOff = null;
    shell.ghost(null);
    if (needsKey(route) && !session.key('A')) {
      router.go({ name: 'connect' }, true);
      return;
    }
    for (const owner of [...layouts.keys()]) if (owner !== 'reel') layouts.delete(owner);
    selection = [];
    pushScene();
    // What was said about another World (a refusal's sentence, its mark) does not follow the visitor here.
    fx.conductor.play({ kind: 'leave', world: worldOfRoute(route) ?? null });
    if (route.name === 'try') {
      // The try is Connect's to run; the route goes, so the token is out of the address bar first.
      handTry(route.token || null);
      router.go({ name: 'connect' }, true);
      return;
    }
    if (route.name === 'connect' || route.name === 'verify') {
      shell.setMode('connect');
      const slots = shell.stageFor(route);
      screenOff = lazily('connect', slots.stage, SCREENS.connect, {});
      return;
    }
    shell.setMode('app');
    ensurePanels();
    const slots = shell.stageFor(route);
    const world = worldOfRoute(route);
    // The World in view keeps a socket; the pool keeps the few seen lately and closes the oldest past eight.
    if (world) links.open(world, { presence: true });
    const parts: Unmount[] = [];
    switch (route.name) {
      case 'stage':
        parts.push(lazily('stage', slots.stage, SCREENS.constellation, {}));
        break;
      case 'world': {
        const p = { world: route.world, ...(route.date ? { date: route.date } : {}), ...(route.hour !== undefined ? { hour: route.hour } : {}) };
        parts.push(lazily('field', slots.field!, SCREENS.field, p));
        parts.push(lazily('river', slots.river!, SCREENS.river, { world: route.world }));
        parts.push(lazily('roster', slots.roster!, SCREENS.roster, { world: route.world }));
        break;
      }
      case 'relay':
        parts.push(lazily('loom', slots.stage, SCREENS.loom, { world: route.world, relay: route.relay }));
        parts.push(lazily('roster', slots.roster!, SCREENS.roster, { world: route.world }));
        break;
      case 'inbox':
        parts.push(lazily('inbox', slots.stage, SCREENS.inbox, { world: route.world }));
        parts.push(lazily('roster', slots.roster!, SCREENS.roster, { world: route.world }));
        break;
      case 'gallery':
        parts.push(lazily('gallery', slots.stage, SCREENS.gallery, {}));
        break;
      case 'account':
        parts.push(lazily('account', slots.stage, SCREENS.account, {}));
        break;
    }
    screenOff = () => { for (const off of parts.reverse()) off(); };
    // The opening (§4.3): the first time the working stage shows, the sea condenses and the Worlds ignite.
    if (!opened) {
      opened = true;
      requestAnimationFrame(() => fx.opening());
    }
    if (world) sayFocus(world);
  };
  /** Where this tab is, on the focused World's socket (again when that socket goes live). */
  sayFocus = (world) => {
    const f = focus();
    if (f.world !== world) return;
    presence({ t: 'focus', view: router.current().name, ...(f.date ? { date: f.date } : {}), ...(f.hour !== undefined ? { hour: f.hour } : {}) }, world);
  };
  router.on(render);

  // -- keys the whole app answers
  document.addEventListener('keydown', (e) => {
    const app = appRoot.querySelector<HTMLElement>('.app')?.dataset.mode === 'app';
    if (!app) return;
    // A sheet that asked something keeps the keys until it is answered.
    if (modalOpen(document)) return;
    if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
      e.preventDefault();
      openPalette();
      return;
    }
    // A sheet, a card, the ring or the hand that took the key (Esc on a confirm sheet) keeps it.
    if (e.defaultPrevented || isTyping(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
    if ((e.key === 'm' || e.key === 'M') && e.shiftKey) {
      e.preventDefault();
      transport.toggle();
    } else if (e.key === 'm') {
      e.preventDefault();
      shell.toggleDrawer('mcp');
    } else if (e.key === '/') {
      // The Director's prompt, always (never the "use case title" beside it), its words selected to type over.
      const input = shell.director.querySelector<HTMLTextAreaElement>('.dir-input');
      if (input) { e.preventDefault(); input.focus(); input.select(); }
    } else if (e.key === '?') {
      e.preventDefault();
      showGlossary(sheets, (to) => router.go(to));
    } else if ((e.key === 'g' || e.key === 'G') && !e.shiftKey && !(e.target as Element | null)?.closest?.('.pg-field, .pg-loom, .pg-inbox')) {
      // G opens the Gallery (a World's field keeps G for its own give-up).
      e.preventDefault();
      router.go({ name: 'gallery' });
    } else if ((e.key === 'i' || e.key === 'I') && !e.shiftKey) {
      e.preventDefault();
      shell.inspect('toggle');
    } else if ((e.key === 'w' || e.key === 'W') && !e.shiftKey) {
      e.preventDefault();
      shell.wire.querySelector<HTMLButtonElement>('.wire-ticker')?.click();
    } else if (e.key === 'Escape' && g.state().phase === 'idle') {
      if (shell.dismiss()) e.preventDefault();
      else if (!shell.director.querySelector('.dir[data-log]')) {
        // Nothing left floating (the Director's log closes on this Esc itself): Esc steps back out, a relay or an
        // inbox to its World, a World (or a page) to the stage.
        const r = router.current();
        if (r.name === 'relay' || r.name === 'inbox') { e.preventDefault(); router.go({ name: 'world', world: r.world }); }
        else if (r.name === 'world' || r.name === 'gallery' || r.name === 'account') { e.preventDefault(); router.go({ name: 'stage' }); }
      }
    }
  });

  render(router.current());
}

if (typeof document !== 'undefined') void boot();
